import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  CaretLeft,
  CaretRight,
  Check,
  BellSimple,
  ListChecks,
  CheckCircle,
  EnvelopeSimpleOpen,
  Confetti,
} from "@phosphor-icons/react";
import iconOnly from "../../../assets/icon_only_new.png";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import { useMarkAsReadMutation } from "../../../store/services/notificationsApi";
import { useUpdateTaskMutation } from "../../../store/services/tasksApi";
import ModalCloseButton from "../../ui/ModalCloseButton";
import PendingActionCard from "./PendingActionCard";
import useGenderText from "./useGenderText";
import "./CatchUpMode.css";

const SWIPE_THRESHOLD = 50;

const TASK_STATUS_LABEL = {
  todo: "פתוח",
  in_progress: "בתהליך",
  waiting: "ממתין",
  done: "הסתיים",
};

function formatNotifTime(value) {
  if (!value) return "";
  try {
    const d = new Date(value.toDate ? value.toDate() : value);
    return d.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * CatchUpMode
 * --------------------------------------------------------------
 * Focused "catch up" experience — full-screen on mobile, a large centered
 * panel on desktop. One interactive card at a time with a clean, dedicated
 * design per item type.
 *
 * Navigation order is snapshotted on open (stable), but each card's DATA is
 * read LIVE from `items` (essential for the meeting step flow). Acting on a
 * card drops it (via `resolvedIds` + the live list); "skip" just advances.
 *
 * `inline` renders the deck embedded on the page (no overlay / no close button)
 * and prepends a welcome "intro" card; the count always reflects real items.
 */
const INTRO_ID = "__intro__";

export default function CatchUpMode({ items, onClose, inline = false }) {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const g = useGenderText();

  const [markAsRead] = useMarkAsReadMutation();
  const [updateTask] = useUpdateTaskMutation();

  const [order] = useState(() => {
    const ids = (items || []).map((it) => it.id);
    return inline ? [INTRO_ID, ...ids] : ids;
  });
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dir, setDir] = useState("next"); // last navigation direction (for the slide animation)
  const touchStartX = useRef(null);

  const byId = useMemo(() => {
    const m = new Map();
    if (inline) m.set(INTRO_ID, { type: "intro", id: INTRO_ID });
    (items || []).forEach((it) => m.set(it.id, it));
    return m;
  }, [items, inline]);

  const visible = useMemo(
    () => order.filter((id) => byId.has(id) && !resolvedIds.has(id)),
    [order, byId, resolvedIds]
  );

  const total = visible.length;
  // Count shown to the user excludes the intro card.
  const realTotal = useMemo(
    () => visible.filter((id) => id !== INTRO_ID).length,
    [visible]
  );

  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, total - 1)));
  }, [total]);

  const goNext = useCallback(() => {
    setDir("next");
    setIndex((i) => Math.min(i + 1, total - 1));
  }, [total]);

  const goPrev = useCallback(() => {
    setDir("prev");
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  const resolve = useCallback((id) => {
    setDir("next");
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goNext();
      else if (e.key === "ArrowRight") goPrev();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, onClose]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta <= -SWIPE_THRESHOLD) goNext();
    else if (delta >= SWIPE_THRESHOLD) goPrev();
    touchStartX.current = null;
  };

  const safeIndex = Math.min(index, Math.max(0, total - 1));
  const current = byId.get(visible[safeIndex]);

  // ---- Notification action ----
  const handleMarkNotificationRead = async (item) => {
    setBusy(true);
    try {
      await markAsRead({ clinicId, notificationId: item.data.id, therapistId }).unwrap();
      resolve(item.id);
    } catch {
      /* mutation logs its own CK code */
    } finally {
      setBusy(false);
    }
  };

  // ---- Task action ----
  const handleCompleteTask = async (item) => {
    setBusy(true);
    try {
      await updateTask({
        clinicId,
        taskId: item.data.id,
        taskData: { status: "done" },
        editorId: therapistId,
        editorName: therapistName,
      }).unwrap();
      resolve(item.id);
    } catch {
      /* mutation logs its own CK code */
    } finally {
      setBusy(false);
    }
  };

  const renderCard = () => {
    if (!current) return null;

    if (current.type === "intro") {
      return (
        <div className="cu-card intro">
          <div className="cu-intro-body">
            <span className="cu-intro-icon">
              <img src={iconOnly} alt="" className="cu-intro-logo" />
            </span>
            <h3 className="cu-intro-title">{g("לחץ כאן כדי להתעדכן", "לחצי כאן כדי להתעדכן")}</h3>
            <p className="cu-intro-sub">
              {realTotal > 0
                ? `${realTotal} ${realTotal === 1 ? "עדכון ממתין" : "עדכונים ממתינים"} לך`
                : g("בוא נעבור על מה שהצטבר", "בואי נעבור על מה שהצטבר")}
            </p>
          </div>

          <div className="cu-actions">
            <button className="cu-btn cu-btn-primary" onClick={goNext}>
              <span>{g("בוא נתחיל", "בואי נתחיל")}</span>
              <CaretLeft size={18} weight="bold" />
            </button>
          </div>
        </div>
      );
    }

    if (current.type === "meeting") {
      return (
        <PendingActionCard
          key={current.id}
          appointment={current.data}
          onResolved={() => resolve(current.id)}
        />
      );
    }

    if (current.type === "notification") {
      const n = current.data;
      return (
        <div className="cu-card notification">
          <div className="cu-card-head">
            <BellSimple size={15} weight="fill" />
            <span>התראת מערכת</span>
            <span className="cu-card-head-meta">
              {formatNotifTime(n.timestamp || n.createdAt)}
            </span>
          </div>

          <div className="cu-msg">
            <h3 className="cu-id-name">{n.title}</h3>
            {n.message && <p className="cu-id-text">{n.message}</p>}
          </div>

          <div className="cu-actions">
            <button
              className="cu-btn cu-btn-primary"
              disabled={busy}
              onClick={() => handleMarkNotificationRead(current)}
            >
              <EnvelopeSimpleOpen size={18} weight="bold" />
              <span>{g("סמן כנקרא", "סמני כנקרא")}</span>
            </button>
            <button className="cu-btn cu-btn-ghost" disabled={busy} onClick={goNext}>
              <span>{g("השאר כלא נקרא", "השאירי כלא נקרא")}</span>
            </button>
          </div>
        </div>
      );
    }

    if (current.type === "task") {
      const t = current.data;
      return (
        <div className="cu-card task">
          <div className="cu-card-head">
            <ListChecks size={15} weight="fill" />
            <span>משימה</span>
            <span className="cu-card-head-meta">
              {TASK_STATUS_LABEL[t.status] || t.status}
            </span>
          </div>

          <div className="cu-msg">
            <h3 className="cu-id-name">{t.title}</h3>
            {t.note && <p className="cu-id-text">{t.note}</p>}
          </div>

          <div className="cu-actions">
            <button
              className="cu-btn cu-btn-primary"
              disabled={busy}
              onClick={() => handleCompleteTask(current)}
            >
              <CheckCircle size={18} weight="bold" />
              <span>{g("סמן כהושלם", "סמני כהושלם")}</span>
            </button>
            <button className="cu-btn cu-btn-ghost" disabled={busy} onClick={goNext}>
              <span>{g("דלג", "דלגי")}</span>
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  // In inline mode the deck is "done" once no real items remain (intro aside).
  const showDone = inline ? realTotal === 0 : total === 0;
  const atFirst = safeIndex <= 0;
  const atLast = safeIndex >= total - 1;
  const countLabel = `${realTotal} ${realTotal === 1 ? "נותר" : "נותרו"}`;

  const deck = (
      <div
        className={`cu-mode${inline ? " cu-inline" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="cu-top">
          {!inline && (
            <div className="cu-top-side">
              <ModalCloseButton onClick={onClose} />
            </div>
          )}
          {!showDone && (
            <div className="cu-top-center">
              <button
                type="button"
                className="cu-top-nav"
                onClick={goPrev}
                disabled={atFirst}
                aria-label="הקודם"
              >
                <CaretRight size={18} weight="bold" />
              </button>
              <span className="cu-top-count">{countLabel}</span>
              <button
                type="button"
                className="cu-top-nav"
                onClick={goNext}
                disabled={atLast}
                aria-label="הבא"
              >
                <CaretLeft size={18} weight="bold" />
              </button>
            </div>
          )}
          <div className="cu-top-side end" />
        </div>

        {showDone ? (
          <div className="cu-done">
            <Confetti size={52} weight="duotone" />
            <p className="cu-done-title">סיימת לעדכן הכל!</p>
            <p className="cu-done-sub">אין פריטים נוספים שדורשים את תשומת ליבך כרגע.</p>
            {!inline && (
              <button className="cu-btn cu-btn-primary" onClick={onClose}>
                <Check size={18} weight="bold" />
                <span>סגירה</span>
              </button>
            )}
          </div>
        ) : (
          <div className="cu-stage">
            <div className={`cu-card-anim ${dir}`} key={current ? current.id : "none"}>
              {renderCard()}
            </div>
          </div>
        )}
      </div>
  );

  if (inline) return deck;

  return (
    <div className="cu-overlay" onClick={onClose}>
      {deck}
    </div>
  );
}
